import React, { useEffect, useMemo, useRef } from 'react';
import { css, cx } from '@emotion/css';
import { useUniqueId } from '../utils/useUniqueID';
import { useDescribers } from '../utils/useDescribers';
import { Help } from '../InputModules/Help';
import { InputBottomRow } from '../InputModules/InputBottomRow';
import { ComboboxInput, ListboxPortal } from '../SelectModules';
import { determineItemValue, setupSafelyCallItemToID, useDetermineFilteredListItems, useMultiSelectHooks, } from '../SelectModules/utils';
import { inputContainerStyles, inputTopRowStyles, } from '../shared';
import { validateInputLabelAccessibility } from '../utils/helperFunctions';
const selectInputContainerStyles = css `
  box-sizing: border-box;
  position: relative;
`;
/**
 * Add new items when `value` prop updates, verifying
 * new items don't already exist as selected items
 * @param safelyCallItemToID - function to convert item to ID
 * @param addSelectedItem - adds item to MultiSelect, derived from `useMultipleSelection` hook
 * @param newItems - array of newly selected items
 * @param selectedItems - array of currently selected items
 */
export const addSelections = (safelyCallItemToID, addSelectedItem, newItems, selectedItems = []) => {
    const itemToID = safelyCallItemToID;
    const selectedItemIds = selectedItems.map((item) => itemToID(item));
    newItems
        .filter((newItem) => !selectedItemIds.includes(itemToID(newItem)))
        .forEach((newItem) => addSelectedItem(newItem));
};
export const MultiSelect = ({ AllowCreateProps, allowItemTextWrap, 'aria-label': ariaLabel, className, customRender, error, guidance, help, id, inputHeight, initialSelectedItems = [], innerInputRef, innerWrapperRef, isAsync, isDisabled, isLoading, isRequired, itemIsAnchored, itemIsDisabled, items = [], itemToID, itemToString, label, listboxWidth, maxHeight, onBlur, onChange, onInputChange, onInternalStateChange, onListboxScroll, placeholder, placeholderTag, shouldShowAdditionalResultsMessage, showClearAllIcon, style, testId, units, useCheckboxSelection, value, ...props }) => {
    validateInputLabelAccessibility('MultiSelect', label, ariaLabel);
    const dropdownWrapperRef = useRef(null);
    const multiSelectInnerInputRef = useRef(null);
    const selectInputMultiSelectRef = innerInputRef || multiSelectInnerInputRef;
    const uniqueIdMultiSelect = useUniqueId(id);
    const describersMultiSelect = useDescribers({ help, uniqueId: uniqueIdMultiSelect, guidance });
    const renderItemToStringMultiSelect = itemToString || determineItemValue;
    const { filteredAnchoredItemsCount, listItems, updateFilteredListboxItems, setListItems } = useDetermineFilteredListItems(useCheckboxSelection);
    const updateFilteredItemsMultiSelect = (inputText = inputValue, currentlySelected = selectedItems) => {
        updateFilteredListboxItems({
            itemIsAnchored,
            items,
            itemToID,
            renderItemToString: renderItemToStringMultiSelect,
        }, inputText, currentlySelected);
    };
    const clearSelection = () => {
        setSelectedItems([]);
    };
    const safelyCallItemToID = (item) => {
        return setupSafelyCallItemToID(itemToID, renderItemToStringMultiSelect, item);
    };
    const { useComboboxControlProps, useMultipleSelectionControlProps } = useMultiSelectHooks({
        initialSelectedItems,
        isDisabled,
        itemIsDisabled,
        listItems,
        onChange,
        onInputChange,
        onInternalStateChange,
        renderItemToString: renderItemToStringMultiSelect,
        selectInput: selectInputMultiSelectRef,
        updateFilteredItems: updateFilteredItemsMultiSelect,
        safelyCallItemToID,
    });
    const { getInputProps, getItemProps, getLabelProps, getMenuProps, getToggleButtonProps, highlightedIndex, inputValue, isOpen, openMenu, } = useComboboxControlProps;
    const { addSelectedItem, getDropdownProps, getSelectedItemProps, removeSelectedItem, selectedItems, setSelectedItems, } = useMultipleSelectionControlProps;
    const isAllowCreate = useMemo(() => {
        if (!AllowCreateProps?.allowCreate || !inputValue)
            return false;
        if (items.length) {
            const firstItem = items[0];
            if (typeof firstItem === 'string') {
                console.error('AllowCreateProps is not compatible with string items. Please use object items with `id` and `name` properties.');
                return false;
            }
            else if (!Object.prototype.hasOwnProperty.call(firstItem, 'id') ||
                !Object.prototype.hasOwnProperty.call(firstItem, 'name')) {
                console.error('AllowCreateProps is not compatible with items that do not have `id` and `name` properties.');
                return false;
            }
        }
        const isItemExists = listItems.some((item) => item.id.toLowerCase() === inputValue.toLowerCase() && !item.isCreateable);
        const isItemSelected = selectedItems.some((item) => item.id.toLowerCase() === inputValue.toLowerCase());
        return AllowCreateProps?.allowCreate && !!inputValue && !isItemExists && !isItemSelected;
    }, [AllowCreateProps?.allowCreate, inputValue, listItems, items, selectedItems]);
    const isCreateExist = useMemo(() => listItems.some((item) => item.isCreateable), [listItems]);
    // Add a create item when in allow create
    useEffect(() => {
        if (isAllowCreate && !isCreateExist) {
            setListItems((prevState) => [
                ...prevState,
                { id: inputValue, name: inputValue, isCreateable: true },
            ]);
        }
    }, [isAllowCreate, listItems, setListItems, inputValue, isCreateExist]);
    // Update items on initial render and when `items` is changed
    useEffect(() => {
        if (items.length) {
            updateFilteredItemsMultiSelect(inputValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);
    // If value prop changes, update selection
    useEffect(() => {
        if (value) {
            if (value.length > 0) {
                addSelections(safelyCallItemToID, addSelectedItem, value, selectedItems);
            }
            else if (selectedItems.length > 0) {
                clearSelection();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (React.createElement("div", { className: cx(inputContainerStyles, selectInputContainerStyles, 'ds-multi-select'), style: style, "data-testid": `${testId}-container`, ref: innerWrapperRef, ...props },
        React.createElement("div", { className: inputTopRowStyles, "data-testid": `${testId}-top-row` },
            React.createElement(Help, { id: uniqueIdMultiSelect, testId: testId || uniqueIdMultiSelect, ...help })),
        React.createElement(ComboboxInput, { ariaLabel: ariaLabel, uniqueId: uniqueIdMultiSelect, testId: testId || uniqueIdMultiSelect, selectInput: selectInputMultiSelectRef, placeholder: placeholder, openMenu: openMenu, onBlur: onBlur, label: label, itemToString: renderItemToStringMultiSelect, isRequired: isRequired, isOpen: isOpen, isDisabled: isDisabled, getToggleButtonProps: getToggleButtonProps, getSelectedItemProps: getSelectedItemProps, getLabelProps: getLabelProps, getInputProps: getInputProps, error: error, describers: describersMultiSelect, dropdownWrapperRef: dropdownWrapperRef, clearSelection: clearSelection, className: className, inputHeight: inputHeight, inputValue: inputValue || '', multiSelectProps: {
                isMultiSelect: true,
                isRegularMultiSelect: !useCheckboxSelection,
                isCheckboxMultiSelect: useCheckboxSelection,
                getDropdownProps,
                selectedItems,
                removeSelectedItem,
                placeholderTag,
                showClearAllIcon,
            } }),
        React.createElement(ListboxPortal, { AllowCreateProps: AllowCreateProps, totalItemCount: listItems.length, testId: testId || uniqueIdMultiSelect, shouldShowAdditionalResultsMessage: shouldShowAdditionalResultsMessage, onListboxScroll: onListboxScroll, maxHeight: maxHeight, listboxWidth: listboxWidth, itemToString: renderItemToStringMultiSelect, itemToID: itemToID, itemIsDisabled: itemIsDisabled, isOpen: isOpen, isLoading: isLoading, isDisabled: isDisabled, isAsync: isAsync, inputValue: inputValue || '', id: uniqueIdMultiSelect, highlightedIndex: highlightedIndex, getMenuProps: getMenuProps, getItemProps: getItemProps, filteredItems: listItems, filteredAnchoredItemsCount: filteredAnchoredItemsCount, customRender: customRender, dropdownWrapperRef: dropdownWrapperRef, allowItemTextWrap: allowItemTextWrap, selectedItems: selectedItems, useCheckboxSelection: useCheckboxSelection }),
        React.createElement(InputBottomRow, { units: units, testId: testId || uniqueIdMultiSelect, isDisabled: isDisabled, id: uniqueIdMultiSelect, guidance: guidance, error: error })));
};
MultiSelect.displayName = 'MultiSelect';
